import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { finalize, forkJoin, Observable, Subscription, tap } from 'rxjs';
import { AutoCompleteModel } from '../../../core/component/autocomplete/autocomplete-model';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { ErrandType } from '../../../round-planning/model/const/enums';
import {
  ErrandAssignmentOption,
  ErrandAssignmentOptions,
  ErrandTag,
  PropertyErrandTagResponsibility,
} from '../../../standalone-errand/model/errand-tag';
import { ErrandTagService } from '../../../standalone-errand/service/data/errand-tag.service';

interface ResponsibilityRowContext {
  form: FormGroup;
  historicalReadonly: boolean;
  tagSelection: SelectionItem<string>[];
  resources: AutoCompleteModel[];
  executorGroups: AutoCompleteModel[];
  executorWebUsers: AutoCompleteModel[];
  subscriptions: Subscription;
  optionRequest?: Subscription;
}

@Component({
  selector: 'app-property-errand-tag-responsibility',
  templateUrl: './property-errand-tag-responsibility.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PropertyErrandTagResponsibilityComponent implements OnChanges, OnDestroy {
  @Input() propertyId: string;

  rows: ResponsibilityRowContext[] = [];
  loading = false;
  saving = false;
  private structureDirty = false;
  private tags: ErrandTag[] = [];
  private readonly subscriptions = new Subscription();
  private readonly duplicateTagValidator: ValidatorFn = control => {
    const tagId = control.value;
    if (!tagId) return null;

    const duplicated = this.rows.some(
      row => row.form.controls['errandTagId'] !== control && row.form.controls['errandTagId'].value === tagId
    );
    return duplicated
      ? { duplicateResponsibilityTag: { message: 'ERRAND_TAG.DUPLICATE_RESPONSIBILITY' } }
      : null;
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly controls: FormControlExFactory,
    private readonly service: ErrandTagService,
    private readonly messages: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyId'] && this.propertyId) this.load();
  }

  ngOnDestroy(): void {
    this.clearRows();
    this.subscriptions.unsubscribe();
  }

  add(): void {
    this.rows.push(this.createRow());
    this.structureDirty = true;
    this.refreshTagState();
  }

  remove(index: number): void {
    if (this.rows[index].historicalReadonly) return;

    this.rows[index].optionRequest?.unsubscribe();
    this.rows[index].subscriptions.unsubscribe();
    this.rows.splice(index, 1);
    this.structureDirty = true;
    this.refreshTagState();
  }

  get canAddResponsibility(): boolean {
    const selectedTagIds = new Set(this.rows.map(row => row.form.controls['errandTagId'].value).filter(Boolean));
    return this.tags.some(tag => this.isAssignableTag(tag) && !selectedTagIds.has(tag.id));
  }

  get invalid(): boolean {
    return this.rows.some(row => row.form.invalid);
  }

  get dirty(): boolean {
    return this.structureDirty || this.rows.some(row => row.form.dirty);
  }

  validate(): boolean {
    this.refreshDuplicateValidation();
    if (!this.invalid) return true;

    this.rows.forEach(row => FormComponentBase.markAsTouched(row.form));
    return false;
  }

  saveChanges(): Observable<PropertyErrandTagResponsibility[]> {
    this.saving = true;
    return this.service.saveResponsibilities(this.propertyId, this.toPayload()).pipe(
      tap(saved => this.replaceRows(saved)),
      finalize(() => (this.saving = false))
    );
  }

  private load(): void {
    this.loading = true;
    this.subscriptions.add(
      forkJoin({ tags: this.service.getAll(), responsibilities: this.service.getResponsibilities(this.propertyId) }).subscribe({
        next: result => {
          this.loading = false;
          this.tags = result.tags ?? [];
          this.replaceRows(result.responsibilities);
        },
        error: error => {
          this.loading = false;
          this.messages.showError(error);
        },
      })
    );
  }

  private replaceRows(models: PropertyErrandTagResponsibility[]): void {
    this.clearRows();
    this.rows = (models ?? []).map(model => this.createRow(model));
    this.structureDirty = false;
    this.refreshTagState();
  }

  private clearRows(): void {
    this.rows.forEach(row => {
      row.optionRequest?.unsubscribe();
      row.subscriptions.unsubscribe();
    });
    this.rows = [];
  }

  private createRow(model?: PropertyErrandTagResponsibility): ResponsibilityRowContext {
    const historicalReadonly = this.isHistoricalReadonly(model);
    const tagControl = this.controls.createMandatory('ERRAND_TAG.NAME', model?.errandTagId);
    const resourceControl = this.controls.create(
      'FAULT_NOTIFICATION.RESOURCE',
      historicalReadonly ? this.savedOption(model?.resourceName, model?.resourceId) : null
    );
    const executorGroupControl = this.controls.create(
      'ERRANDLIST_EXECUTORGROUPS',
      historicalReadonly ? this.savedOption(model?.executorGroupName, model?.executorGroupId) : null
    );
    const executorWebUserControl = this.controls.create(
      'ERRANDLIST_WEBUSERS',
      historicalReadonly ? this.savedOption(model?.executorWebUserName, model?.executorWebUserId) : null
    );
    tagControl.addValidators(this.duplicateTagValidator);
    [tagControl, resourceControl, executorGroupControl, executorWebUserControl]
      .forEach(control => (control.isReadonly = historicalReadonly));
    const context: ResponsibilityRowContext = {
      form: this.fb.group({
        id: this.controls.createHidden(model?.id),
        errandTagId: tagControl,
        resourceId: resourceControl,
        executorGroupId: executorGroupControl,
        executorWebUserId: executorWebUserControl,
      }),
      historicalReadonly,
      tagSelection: [],
      resources: resourceControl.value ? [resourceControl.value] : [],
      executorGroups: executorGroupControl.value ? [executorGroupControl.value] : [],
      executorWebUsers: executorWebUserControl.value ? [executorWebUserControl.value] : [],
      subscriptions: new Subscription(),
    };

    if (historicalReadonly) return context;

    context.subscriptions.add(
      context.form.controls['errandTagId'].valueChanges.subscribe(tagId => {
        context.form.patchValue({ resourceId: null, executorGroupId: null, executorWebUserId: null }, { emitEvent: false });
        this.loadOptions(context, tagId);
        this.refreshTagState();
      })
    );
    context.subscriptions.add(
      context.form.controls['executorGroupId'].valueChanges.subscribe(groupId => {
        const currentResource = context.form.controls['resourceId'].value;
        const currentUser = context.form.controls['executorWebUserId'].value;
        this.loadOptions(
          context,
          context.form.controls['errandTagId'].value,
          groupId?.id,
          currentUser?.id,
          currentResource?.id
        );
      })
    );

    if (model?.errandTagId) {
      this.loadOptions(context, model.errandTagId, model.executorGroupId, model.executorWebUserId, model.resourceId);
    }
    return context;
  }

  private isHistoricalReadonly(model?: PropertyErrandTagResponsibility): boolean {
    if (!model?.errandTagId) return false;

    const tag = this.tags.find(item => item.id === model.errandTagId);
    return !tag || !tag.isEnabled;
  }

  private savedOption(name?: string, id?: string): AutoCompleteModel | null {
    return id ? new AutoCompleteModel(name || id, id) : null;
  }

  private refreshTagState(): void {
    const selectedTagIds = new Set(this.rows.map(row => row.form.controls['errandTagId'].value).filter(Boolean));
    this.rows.forEach(row => {
      const currentTagId = row.form.controls['errandTagId'].value;
      if (row.historicalReadonly) {
        const currentTag = this.tags.find(tag => tag.id === currentTagId);
        row.tagSelection = currentTagId
          ? [new SelectionItem(currentTag?.name || currentTagId, currentTagId)]
          : [];
        return;
      }

      row.tagSelection = this.tags
        .filter(tag => {
          if (tag.id === currentTagId) return this.isAssignableTag(tag);
          return this.isAssignableTag(tag) && !selectedTagIds.has(tag.id);
        })
        .map(tag => new SelectionItem(tag.name, tag.id));
    });
    this.refreshDuplicateValidation();
  }

  private refreshDuplicateValidation(): void {
    this.rows.forEach(row =>
      row.form.controls['errandTagId'].updateValueAndValidity({ emitEvent: false })
    );
  }

  private isAssignableTag(tag: ErrandTag): boolean {
    return tag.isEnabled && tag.accessGroupCount > 0;
  }

  private loadOptions(
    context: ResponsibilityRowContext,
    tagId?: string,
    executorGroupId?: string,
    currentExecutorWebUserId?: string,
    currentResourceId?: string
  ): void {
    context.optionRequest?.unsubscribe();
    if (!tagId) {
      context.resources = [];
      context.executorGroups = [];
      context.executorWebUsers = [];
      return;
    }

    const request = (errandType: ErrandType) =>
      this.service.getAssignmentOptions(this.propertyId, tagId, errandType, executorGroupId);
    context.optionRequest = forkJoin([request(ErrandType.TenantRequest), request(ErrandType.GenericErrand)]).subscribe({
        next: options => {
          context.resources = this.mergeOptions(options, item => item.resources);
          context.executorGroups = this.mergeOptions(options, item => item.executorGroups);
          context.executorWebUsers = this.mergeOptions(options, item => item.executorWebUsers);
          context.form.patchValue({
            resourceId: this.findOption(context.resources, currentResourceId),
            executorGroupId: this.findOption(context.executorGroups, executorGroupId),
            executorWebUserId: this.findOption(context.executorWebUsers, currentExecutorWebUserId),
          }, { emitEvent: false });
        },
        error: error => this.messages.showError(error),
      });
    context.subscriptions.add(context.optionRequest);
  }

  private findOption(options: AutoCompleteModel[], id?: string): AutoCompleteModel | null {
    return id ? options.find(item => item.id === id) ?? null : null;
  }

  private toPayload(): PropertyErrandTagResponsibility[] {
    return this.rows.map(row => {
      const value = row.form.getRawValue();
      return {
        id: value.id,
        errandTagId: value.errandTagId,
        resourceId: value.resourceId?.id ?? null,
        executorGroupId: value.executorGroupId?.id ?? null,
        executorWebUserId: value.executorWebUserId?.id ?? null,
      };
    });
  }

  private mergeOptions(
    sources: ErrandAssignmentOptions[],
    selector: (source: ErrandAssignmentOptions) => ErrandAssignmentOption[]
  ): AutoCompleteModel[] {
    const distinct = new Map<string, ErrandAssignmentOption>();
    sources.flatMap(selector).forEach(item => distinct.set(item.id, item));
    return [...distinct.values()]
      .sort((left, right) => left.name.localeCompare(right.name))
      .map(item => new AutoCompleteModel(item.name, item.id));
  }
}
